import React, { useState, useEffect, useMemo } from 'react';

// Comps
import { TextField } from "@src/stories/elements/TextField/TextField";
import { CustomFields } from '@project-stories/ecommerce/Account/AccountFields/AccountFields';
import { Paragraph } from '@arctheme-components/elements/Paragraph/Paragraph';
import { Link } from '@arctheme-components/elements/Link/Link';
import ButtonSpinner from '@arctheme-components/blocks/ButtonSpinner/ButtonSpinner';
import AccountSectionLayout from '@project-stories/ecommerce/Account/AccountSectionLayout';

// Hooks
import { useGetCustomerQuery } from '@src/hooks/useGetCustomerQuery';
import useCustomerUpdate from '@src/hooks/useCustomerUpdate';

const AccountProfileForm = ({ settings, section, signUpFields }) => {
  const {
    first_name_label,
    first_name_placeholder,
    last_name_label,
    last_name_placeholder,
    email_label,
    email_placeholder,
    button_label,
    button_style,
    success_message
  } = section;

  const { contact } = settings?.globals;

  // Sort custom fields
  const customSignUpFields = signUpFields?.sort((a, b) => {
    const orderA = a.field_order ? parseInt(a.field_order) : Infinity;
    const orderB = b.field_order ? parseInt(b.field_order) : Infinity;
    return orderA - orderB;
  });

  // Hooks
  const { fetchCustomer, customer } = useGetCustomerQuery();
  const {
    updateCustomer,
    loading: updateCustomerLoading,
    success: updateCustomerSuccess,
    error: updateCustomerError,
    fieldsWithError: updateCustomerFieldsWithError
  } = useCustomerUpdate();

  // States
  const initialDependenciesState = useMemo(() => {
    return Object.assign(
      {},
      {
        firstName: '',
        lastName: '',
        email: ''
      },
      ...customSignUpFields.map((field) => ({
        [field.metafield_key]: ''
      }))
    );
  }, [customer]);
  const [dependenciesState, setDependenciesState]: any = useState(initialDependenciesState);
  const [isUpdated, setIsUpdated] = useState(false);

  // Handle input change and update global dependency state
  const handleInputChange = (e) => {
    const { name, value, type: inputType, checked } = e.target;

    // For checkboxes, use the checked value, otherwise use the input value
    const newValue = inputType === 'checkbox' ? checked : value;

    // Update the global dependency state
    updateDependencyState(name, newValue);
  };

  // Function to update the global dependencies state
  const updateDependencyState = (name, value) => {
    const newData = {
      ...dependenciesState,
      [name]: value
    };

    setDependenciesState(newData);
    toggleIsUpdated(newData);
  };

  const toggleIsUpdated = (newData) => {
    const updated = Object.keys(newData).some((key) => customer[key] !== newData[key]);

    setIsUpdated(updated);
  };

  const handleSubmit = (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();

    updateCustomer(dependenciesState, (window as any).customer.id, customSignUpFields);
  };

  // Fetch customer
  useEffect(() => {
    fetchCustomer((window as any).customer.id, customSignUpFields);
  }, []);

  // And then pre-fill the fields
  useEffect(() => {
    if (customer) {
      for (const key in customer) {
        if (key in dependenciesState) {
          setDependenciesState((prev) => ({
            ...prev,
            [key]: customer[key]
          }));
        }
      }
    }
  }, [customer]);

  const showCustomFields = true;

  return (
      <AccountSectionLayout classes='lg:pl-16 xl:pl-[93px] hide-scrollbar'>

        <div className={'flex flex-col lg:flex-row gap-[41px] lg:gap-16 xl:gap-[93px]'}>
          <div className={'flex flex-col w-full lg:max-w-[360px] xl:max-w-[402px] gap-8 '}>
            <div>
              <h4 className="mb-2">
                Overview
              </h4>

              <p className={section.subtitle_style}>
                Personal Information
              </p>

              <form
                  id='editForm'
                  onSubmit={handleSubmit}
                  className='account-page__form mt-8'
              >
                <div className='flex flex-col gap-y-8'>
                  {/* First Name */}
                  <TextField
                      inputType='text'
                      type="floating-input" 
                      id='firstNameRegister'
                      name='firstName'
                      value={dependenciesState['firstName']}
                      onChange={handleInputChange}
                      required={true}
                      disabled={true}
                      label={`${first_name_label}*`}
                      placeholder={first_name_placeholder}
                      hasError={updateCustomerFieldsWithError?.includes('firstName')}
                  />

                  {/* Last Name */}
                  <TextField
                      inputType='text'
                      type="floating-input" 
                      id='lastNameRegister'
                      name='lastName'
                      value={dependenciesState['lastName']}
                      onChange={handleInputChange}
                      required={true}
                      disabled={true}
                      label={`${last_name_label}*`}
                      placeholder={last_name_placeholder}
                      hasError={updateCustomerFieldsWithError?.includes('lastName')}
                  />

                  {/* Email */}
                  <TextField
                      inputType='email'
                      type="floating-input" 
                      id='emailRegister'
                      name='email'
                      value={dependenciesState['email']}
                      onChange={handleInputChange}
                      required={true}
                      disabled={true}
                      label={`${email_label}*`}
                      placeholder={email_placeholder}
                      hasError={updateCustomerFieldsWithError?.includes('email')}
                  />

                  {/* Custom Fields */}
                  {showCustomFields && (
                      <CustomFields
                          customSignUpFields={customSignUpFields}
                          onChange={handleInputChange}
                          values={dependenciesState}
                          fieldsWithError={updateCustomerFieldsWithError}
                      />
                  )}

                  {updateCustomerSuccess && (
                      <Paragraph
                          type='text-center success'
                          text={success_message}
                      />
                  )}

                  {updateCustomerError && (
                      <Paragraph
                          type='errors text-center'
                          text={updateCustomerError}
                      />
                  )}

                  <ButtonSpinner
                      classes={`hidden w-full ${button_style} h-[calc(3rem-2px)] text-base`}
                      isLoading={updateCustomerLoading}
                      ariaLabel='Submit edit profile form'
                      disabled={!isUpdated}
                  >
                    {button_label}
                  </ButtonSpinner>
                </div>
              </form>
            </div>

            <div className={'flex flex-col gap-8'}>
              <div className={`flex flex-col`}>
                <p className={section.subtitle_style}>
                  Order history
                </p>

                <p className={section.subtitle_description_style}>
                  View your recent order history.
                </p>

                <Link
                  classes='button button-secondary w-full mt-6 block'
                  link='/account?view=orders'
                >
                  View orders
                </Link>
              </div>

              <div className={`flex flex-col`}>
                <p className={section.subtitle_style}>
                  Address book
                </p>

                <p className={section.subtitle_description_style}>
                  Save your preferred shipping addresses for a faster checkout.
                </p>

                <Link
                  classes={'button button-secondary w-full mt-6 block'}
                  link={'/account?view=addresses'}
                >
                  View address book
                </Link>
              </div>
            </div>
          </div>

          <div className={`flex flex-col w-full lg:flex-1`}>
            <p className={`${section.contact_us_heading_style} mb-6`}>
              Contact us
            </p>

            <div className={`flex flex-col gap-4`}>
              <p className={section.contact_us_body_style}>
                {contact?.name} <br />
                {contact?.address} <br />
                {contact?.city}, {contact?.state} {contact?.zipcode} <br />
                {contact?.country}
              </p>

              <p className={section.contact_us_body_style}>
                <a
                  href={`tel:+${contact?.phone}`}
                  className="underline"
                >
                  {contact?.phone}
                </a>
              </p>

              <p className={section.contact_us_body_style}>
                <a
                  href="/pages/contact"
                  className="underline"
                >
                  Contact Support
                </a>
              </p>
            </div>
          </div>
        </div>


      </AccountSectionLayout>
  );
};

export default AccountProfileForm;
